<template>
  <div
    :id="carouselId"
    class="main-image-carousel"
  >
    <div v-if="currentPage === 1">
      <transition name="fade">
        <ProductImagesCta
          :product-id="productId"
        />
      </transition>
    </div>
    <template v-if="isAddToListVisible">
      <AddToList
        :container-id="containerId"
        :product-id="productId"
      />
    </template>

    <MainImageSlideshow
      show-shop-similar
      :in-overlay="false"
      :carousel-images="carouselImages"
      :current-page="currentPage"
      :first-alt-dot="firstAltDot"
      :current-page-on-hover="currentPageOnHover"
      :show-indicator="showIndicator"
      @update-carousel="updateCarousel"
      @update-first-alt-dot="updateFirstAltDot"
      @image-click="openImageOverlay"
    />
    <div
      v-if="!isVideoSelected"
      data-test-id="fullscreen-entry"
      tabindex="0"
      class="overlay-entry"
      :class="[fullscreen, { 'single-image': isSingleImage }]"
      @click="openImageOverlay"
      @keydown.enter="openImageOverlay"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import ProductImagesCta from '@components/ProductImagesCta';
import AddToList from '@components/AddToList';
import MainImageSlideshow from '@components/common/Media/MainImageSlideshow';
import { useFeatureEligibility } from '@modules/featureControl';
import { useModal } from '@composables/utils/useModal';
import { useImages } from '@composables/useImages';
import { iconClasses } from 'config';

const props = defineProps({
  currentPage: {
    /** @type {import('vue').PropType<number>} */
    type: Number,
    default: 1,
  },
  firstAltDot: {
    type: Number,
    default: 1,
  },
  currentPageOnHover: {
    /** @type {import('vue').PropType<number | null>} */
    type: Number || null,
    default: null,
  },
  showIndicator: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['updateCarousel', 'updateFirstAltDot']);

const store = useStore();

const { productId } = store.state.pageData;

const { carouselImages, videoIndex } = useImages();
const { isAddToListVisible } = useFeatureEligibility();
const carouselId = computed(() => `main-image-carousel-${productId}`);

const containerId = computed(() => `main-image-${productId}`);
const { open, close } = useModal();
const isSingleImage = computed(() => carouselImages.value.length === 1);

const { fullscreen } = iconClasses;

const openImageOverlay = () => {
  // eslint-disable-next-line
  history.pushState({ imageOverlayOpen: true }, '');
  open('main-image-overlay');
};

/**
 * @type {(...args: unknown[]) => void}
 */
const updateCarousel = (val) => {
  emit('updateCarousel', val);
};
/**
 * @param {number} val
 */
const updateFirstAltDot = (val) => {
  emit('updateFirstAltDot', val);
};

const isVideoSelected = computed(() => (props.currentPage - 1) === videoIndex.value || props.currentPageOnHover === videoIndex.value);

function onPopState() {
  const mainImageOverlayEl = document.querySelector('#main-image-overlay');
  if (mainImageOverlayEl) {
    close('main-image-overlay');
  }
}

onMounted(() => {
  window.addEventListener('popstate', onPopState);
});

onUnmounted(() => {
  window.removeEventListener('popstate', onPopState);
});

</script>

<style lang="scss" scoped>
@import "~settingSCSS";

.main-image-carousel {
  overflow: hidden;
  width: 100%;
  height: fit-content;
  position: relative;
  flex: 1 1 100%;
  @include breakpoint(large up) {
    width: 518px;
  }
}
.overlay-entry {
  position: absolute;
  cursor: pointer;
  bottom: 16px;
  left: 16px;

  @include breakpoint(small only) {
    &.single-image {
        bottom: 16px;
    }
    bottom: 38px;
  }
}
</style>
<style brand="mcom" lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/mcom/_full-screen.scss';
</style>
<style brand="bcom" lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/bcom/_fullscreen.scss';
</style>
